/**
 * WireToSourceDialog — modal launched from the equipment right-click menu.
 * Lets the user pick ANY positioned equipment in the project as the upstream
 * feeder source for the current item (not just the nearest panel-like one).
 *
 * Flow:
 *   1. Modal opens with a searchable, filterable list of every placed
 *      equipment item (excluding self).
 *   2. User clicks one → POST /api/projects/{pid}/equipment-list/{id}/wire-to-source
 *      with { source_tag } → backend persists `fed_from_tag` on the item.
 *   3. Caller's onWired callback receives the updated item + source metadata
 *      so the parent can refresh local state. The user can then visit the
 *      Cables tab to refine voltage / size / breaker etc. for that link.
 *
 * Also exposes an "Auto-pick nearest panel" shortcut button that calls the
 * same endpoint with no source_tag — useful for bulk wiring.
 */
import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Lightning, MagnifyingGlass } from "@phosphor-icons/react";

const FEEDER_HINT = /mcc|panel|switchboard|transformer|distribution|lcp|htcp|control|switchgear|feeder/i;

export default function WireToSourceDialog({
  open, item, projectId, candidates, onClose, onWired,
}) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  // Hooks must run unconditionally — defer the early return until after.
  const filtered = useMemo(() => {
    if (!item) return [];
    const q = query.trim().toLowerCase();
    return (candidates || [])
      .filter((c) => c.id !== item.id && c.position)
      .filter((c) => !q ||
        (c.tag || "").toLowerCase().includes(q) ||
        (c.equipment_type || "").toLowerCase().includes(q))
      .sort((a, b) => {
        // Panels-like first, then alphabetical by tag.
        const ap = FEEDER_HINT.test(a.equipment_type || "") ? 0 : 1;
        const bp = FEEDER_HINT.test(b.equipment_type || "") ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return (a.tag || "").localeCompare(b.tag || "");
      });
  }, [item, candidates, query]);

  if (!open || !item) return null;

  const callWire = async (sourceTag) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/equipment-list/${item.id}/wire-to-source`,
        sourceTag ? { source_tag: sourceTag } : {},
      );
      onWired && onWired(data);
      const dist = data.source?.distance_in != null
        ? ` · ${data.source.distance_in}″` : "";
      toast.success(
        `Wired ${item.tag} ← ${data.source?.tag}${dist}` +
        (data.source?.auto_picked ? " (auto)" : ""),
      );
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wire to source failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1500] bg-black/70 flex items-center justify-center p-4"
         onClick={onClose} data-testid="wire-source-dialog">
      <div className="bg-[#0a0d12] border border-[#FFCC00]/40 w-full max-w-xl flex flex-col font-mono"
           onClick={(e) => e.stopPropagation()}
           style={{ maxHeight: "80vh" }}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="label-tag">// Wire to feeder source</div>
            <div className="text-base text-white inline-flex items-center gap-2 mt-1">
              <Lightning size={16} className="text-[#FFCC00]" />
              Pick the upstream source for{" "}
              <span className="text-[#00E5FF]">{item.tag}</span>
              <span className="text-[10px] text-gray-500">
                {item.equipment_type}
              </span>
            </div>
          </div>
          <button onClick={onClose}
                  className="btn-ghost p-1"
                  data-testid="wire-source-close">
            <X size={14} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-hidden flex flex-col">
          <button
            type="button"
            onClick={() => callWire(null)}
            disabled={busy}
            className="btn-primary text-sm py-2 inline-flex items-center gap-2 self-start disabled:opacity-40"
            data-testid="wire-source-auto">
            <Lightning size={14} weight="fill" />
            Auto-pick nearest panel
          </button>

          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by tag or type…"
              className="input-base text-sm w-full pl-8"
              data-testid="wire-source-search"
            />
          </div>

          <div className="text-[10px] text-gray-500">
            {filtered.length} placed equipment item{filtered.length === 1 ? "" : "s"}
            {" "}available — pick one to set as the feeder source, then update
            the cable details in the Cables tab.
          </div>

          <div className="border border-white/10 overflow-y-auto divide-y divide-white/5"
               style={{ maxHeight: "45vh" }}
               data-testid="wire-source-list">
            {filtered.length === 0 ? (
              <div className="text-[11px] text-yellow-300 p-3">
                No other positioned equipment in this project. Place at least
                one panel-like item (MCC, Panelboard, Transformer…) before
                wiring this load.
              </div>
            ) : filtered.map((c) => {
              const isPanel = FEEDER_HINT.test(c.equipment_type || "");
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={busy}
                  onClick={() => callWire(c.tag)}
                  className="w-full text-left p-2.5 text-[11px] transition-colors hover:bg-[#FFCC00]/10 disabled:opacity-40 flex items-center gap-3"
                  data-testid={`wire-source-pick-${c.tag}`}>
                  <span className={`px-1.5 py-0.5 text-[9px] font-bold ${
                    isPanel
                      ? "bg-[#FFCC00] text-black"
                      : "bg-white/10 text-gray-300"
                  }`}>
                    {isPanel ? "PANEL" : "LOAD"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#00E5FF] truncate">{c.tag}</div>
                    <div className="text-[10px] text-gray-500 truncate">
                      {c.equipment_type || "—"}
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-500 shrink-0">
                    @ {Math.round(c.position?.x_in || 0)}″, {Math.round(c.position?.y_in || 0)}″
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
